import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { Activity, ChevronLeft, ChevronRight, Loader2, X } from 'lucide-react';

const ACTION_CONFIG = {
  login:              { label: 'Login',               color: 'bg-gray-500' },
  job_create:         { label: 'Posted Job',           color: 'bg-green-600' },
  job_edit:           { label: 'Edited Job',           color: 'bg-blue-500' },
  job_delete:         { label: 'Deleted Job',          color: 'bg-red-500' },
  job_boost:          { label: 'Boosted Job',          color: 'bg-amber-500' },
  job_renew:          { label: 'Renewed Job',          color: 'bg-amber-500' },
  job_filled:         { label: 'Marked Filled',        color: 'bg-green-700' },
  view_application:   { label: 'Viewed Applicant',     color: 'bg-purple-500' },
  application_decision: { label: 'Answered Applicant', color: 'bg-teal-600' },
  schedule_interview: { label: 'Scheduled Interview',  color: 'bg-indigo-500' },
  cancel_interview:   { label: 'Cancelled Interview',  color: 'bg-red-400' },
  submit_review:      { label: 'Submitted Review',     color: 'bg-yellow-500' },
  send_message:       { label: 'Sent Message',         color: 'bg-blue-500' },
  purchase_credits:   { label: 'Purchased Credits',    color: 'bg-green-600' },
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.floor(diffHr / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

// Renders a clickable link to a job post, or plain text fallback
function JobLink({ details }) {
  if (!details) return null;
  const title = details.title;
  const slug = details.slug;
  const jobId = details.job_id;

  // Build the URL — prefer slug, fall back to UUID
  const href = slug ? `/jobs/${slug}` : jobId ? `/jobs/${jobId}` : null;

  // Best case: title + link
  if (title && href) {
    return <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{title}</a>;
  }
  // Title but no link
  if (title) return <span>{title}</span>;
  // No title — link with short ID
  if (href) return <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">View job</a>;
  return null;
}

// Renders plain-language details for each action type
function Details({ action, details }) {
  if (!details || Object.keys(details).length === 0) return <span className="text-muted-foreground">-</span>;

  // Any action with job details → show a clickable job link
  if (details.job_id || details.title || details.slug) {
    return <JobLink details={details} />;
  }

  // Interview actions — show job link + applicant name
  if (action === 'schedule_interview' || action === 'cancel_interview') {
    const link = <JobLink details={details} />;
    const name = details.applicant_name;
    if (link && name) return <span>{link} — {name}</span>;
    if (link) return link;
    if (name) return <span>{name}</span>;
    return <span className="text-muted-foreground">-</span>;
  }

  // Review actions — show who was reviewed + rating
  if (action === 'submit_review') {
    const name = details.to_name;
    const rating = details.rating;
    const stars = rating ? `${'★'.repeat(rating)}${'☆'.repeat(5 - rating)}` : '';
    if (name) return <span>{name} {stars}</span>;
    return <span className="text-muted-foreground">{stars || '-'}</span>;
  }

  // Message actions
  if (action === 'send_message') {
    if (details.receiver_name) return <span>To: {details.receiver_name}</span>;
    return <span className="text-muted-foreground">-</span>;
  }

  // Credit purchases
  if (action === 'purchase_credits' && details.pack) {
    return <span>Pack: {details.pack}</span>;
  }

  return <span className="text-muted-foreground">-</span>;
}

export default function EmployerActivityLog() {
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 1 });
  const [actionCounts, setActionCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [employers, setEmployers] = useState([]);

  // Filters
  const [filterUser, setFilterUser] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().slice(0, 10);
  });
  const [filterDateTo, setFilterDateTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [page, setPage] = useState(1);

  const fetchLogs = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const params = new URLSearchParams({ page: p, per_page: 50 });
      if (filterUser) params.set('user_id', filterUser);
      if (filterAction) params.set('action', filterAction);
      if (filterDateFrom) params.set('date_from', filterDateFrom);
      if (filterDateTo) params.set('date_to', filterDateTo);

      const res = await api.get(`/api/admin/activity-log?${params}`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });

      setLogs(res.data.logs || []);
      setPagination(res.data.pagination || { page: 1, total: 0, total_pages: 1 });
      setActionCounts(res.data.action_counts || {});
      setPage(p);
    } catch (err) {
      toast.error('Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  }, [filterUser, filterAction, filterDateFrom, filterDateTo]);

  const fetchEmployers = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const res = await api.get('/api/admin/users', {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      const users = (res.data.users || []).filter(u => u.role === 'employer' || u.role === 'owner');
      setEmployers(users);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => { fetchLogs(1); }, [fetchLogs]);
  useEffect(() => { fetchEmployers(); }, [fetchEmployers]);

  const clearFilters = () => {
    setFilterUser('');
    setFilterAction('');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  const totalActions = Object.values(actionCounts).reduce((s, c) => s + c, 0);
  const hasFilters = filterUser || filterAction || filterDateFrom || filterDateTo;

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{totalActions.toLocaleString()}</p>
          </CardContent>
        </Card>
        {Object.entries(actionCounts).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([action, count]) => (
          <Card key={action}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {ACTION_CONFIG[action]?.label || action}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{count.toLocaleString()}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="w-5 h-5" /> Activity Log
            </CardTitle>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" /> Clear filters
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
            <Select value={filterUser || 'all'} onValueChange={v => setFilterUser(v === 'all' ? '' : v)}>
              <SelectTrigger>
                <SelectValue placeholder="All employers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All employers</SelectItem>
                {employers.map(e => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.business_name || e.name || e.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterAction || 'all'} onValueChange={v => setFilterAction(v === 'all' ? '' : v)}>
              <SelectTrigger>
                <SelectValue placeholder="All actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All actions</SelectItem>
                {Object.entries(ACTION_CONFIG).map(([key, cfg]) => (
                  <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Input
              type="date"
              value={filterDateFrom}
              onChange={e => setFilterDateFrom(e.target.value)}
              placeholder="From date"
            />
            <Input
              type="date"
              value={filterDateTo}
              onChange={e => setFilterDateTo(e.target.value)}
              placeholder="To date"
            />
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : logs.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">No activity logs found</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>Employer</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map(log => {
                    const cfg = ACTION_CONFIG[log.action] || { label: log.action, color: 'bg-gray-400' };
                    const user = log.user || {};
                    return (
                      <TableRow key={log.id}>
                        <TableCell className="whitespace-nowrap text-sm" title={new Date(log.created_at).toLocaleString()}>
                          {timeAgo(log.created_at)}
                        </TableCell>
                        <TableCell className="text-sm">
                          <div className="font-medium">{user.business_name || user.name || '-'}</div>
                          <div className="text-xs text-muted-foreground">{user.role}</div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${cfg.color} text-white text-xs`}>{cfg.label}</Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          <Details action={log.action} details={log.details} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-muted-foreground">
                Page {pagination.page} of {pagination.total_pages} ({pagination.total} total)
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => fetchLogs(page - 1)}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= pagination.total_pages}
                  onClick={() => fetchLogs(page + 1)}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
